
import {
  computed,
  defineComponent,
  PropType,
  ref,
} from "vue";

import { format } from "date-fns";
import * as Locales from 'date-fns/locale';

import EasyDrop from "@/components/EasyDrop.vue";
import EasyAlert from "@/components/EasyAlert.vue";
import DotSpinner from "@/components/EasySpinners/DotSpinner.vue";
import { FormSearchPerson, LocaleStrings, ValueIndexParams } from "@/types/paramModels";
import { SearchBookingOverview, SearchHotelRoom, SearchTravelBookingRequest, SearchTravelBookingResponse } from "@/types/models";
import { useStore } from "vuex";
import TravelService from "@/services/travel.service";
import IResponseData from "@/types/IResponseData";

export default defineComponent({
  name: "TravelOverviewView",
  components: {
    EasyDrop,
    EasyAlert,
    DotSpinner
  },
  props: {
    bookingOverview: {
      type: Object as PropType<SearchBookingOverview>,
      required: true,
    },
    searchQuery: {
      type: Object,
      required: true,
    },
    selectedRooms: {
      type: Array as PropType<Array<SearchHotelRoom>>,
      required: true,
    },
    pageTexts: {
      type: Object,
      required: true
    },
    localeStrings: {
      type: Object as PropType<LocaleStrings>,
      required: true
    }
  },
  setup(_props) {
    const store = useStore();

    const searchBookingOverview = computed(() => _props.bookingOverview);
    const query = computed(() => _props.searchQuery);

    const hasAcceptedConditions = ref(false);

    const addedEmails = computed(() => store.getters['TravelSearchBookingModule/getReceiptEmails']);

    const isShowAlert = ref(false);
    const alertMessage = ref("");

    const paymentProcessing = ref(false);

    const closeAlert = () => {
      isShowAlert.value = false;
    }


    const hasAllValidEmails = computed(() => {
      for (const email of addedEmails.value) {
        if (!isEmailValid(email)) {
          return false;
        }
      }
      return true;
    });

    const addEmail = () => {
      const newEmail = {
        value: "",
        index: addedEmails.value.length,
      } as ValueIndexParams;
      store.dispatch('TravelSearchBookingModule/updateReceiptEmail', newEmail);
    };

    const isEmailValid = (email) => {
      if (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,6})+$/.test(email)) {
        return (true)
      }
      return (false)
    }

    const updateEmail = (event: Event, index: number) => {
      const input = event.target as HTMLInputElement;
      if (isEmailValid(input.value)) {
        document.getElementById("addedEmailWrapper_" + index)?.classList.remove("has-error");
        const errorMsg = document.getElementById("addedEmailError_" + index);
        if (errorMsg) errorMsg.hidden = true;

        const params: ValueIndexParams = { value: input.value, index: index };
        store.dispatch("TravelSearchBookingModule/updateReceiptEmail", params);
      } else {
        document.getElementById("addedEmailWrapper_" + index)?.classList.add("has-error");
        const errorMsg = document.getElementById("addedEmailError_" + index);
        if (errorMsg) errorMsg.hidden = false;
      }
    }

    const hotelLabel = computed(() => {
      return _props.pageTexts['hotelPageBookingConfirmationStepLabelHotelNights'].replace(/\[[nights\]]+\]/g, _props.bookingOverview.travel.hotelNights);
    });

    const deleteAddedEmail = (index: number) => {
      store.dispatch("TravelSearchBookingModule/deleteReceiptEmail", index);
    }

    const formatDate = (dateString: string) => {
      return format(
        new Date(dateString.slice(0, dateString.indexOf("T"))),
        "dd-MM-yyyy",
        { locale: Locales[_props.localeStrings.name] }
      );
    };

    const formatDateTime = (dateString: string) => {
      const date = formatDate(dateString);
      const time = dateString.slice(dateString.indexOf("T") + 1, dateString.indexOf("T") + 6);
      return _props.pageTexts.hotelPageGeneralTimeFormat.trim().length > 0 ? date + " " + _props.pageTexts.hotelPageGeneralTimeFormat.trim() + " " + time : date + " " + time;
    };

    const selectedPensionName = (room: SearchHotelRoom) => {
      const selectedPension = room.pensions.find(
        (pension) => pension.isSelected
      );
      return selectedPension ? selectedPension.name : "";
    };

    const selectedTransfer = computed(() => {
      return _props.bookingOverview.transfers.find((x) => x.isSelected);
    });

    const formAdults = computed(() => {
      const hotelRooms =
        searchBookingOverview.value.bookingSelection.hotelRooms;
      const newAdults = Array<FormSearchPerson>();
      let personTypeIndex = 0;
      for (let roomIndex = 0; roomIndex < hotelRooms.length; roomIndex++) {
        for (
          let personIndex = 0;
          personIndex < hotelRooms[roomIndex].searchRoom.adults.length;
          personIndex++
        ) {
          const newAdult = {
            hotelRoomIndex: roomIndex,
            personIndex: personIndex,
            personTypeIndex: personTypeIndex,
            isAdult: true,
            searchPerson: hotelRooms[roomIndex].searchRoom.adults[personIndex],
          } as FormSearchPerson;
          personTypeIndex++;
          newAdults.push(newAdult);
        }
      }
      return newAdults;
    });

    const formChildren = computed(() => {
      const hotelRooms =
        searchBookingOverview.value.bookingSelection.hotelRooms;
      const newChildren = Array<FormSearchPerson>();
      let personTypeIndex = 0;
      for (let roomIndex = 0; roomIndex < hotelRooms.length; roomIndex++) {
        for (
          let personIndex = 0;
          personIndex < hotelRooms[roomIndex].searchRoom.children.length;
          personIndex++
        ) {
          const newAdult = {
            hotelRoomIndex: roomIndex,
            personIndex: personIndex,
            personTypeIndex: personTypeIndex,
            isAdult: false,
            searchPerson:
              hotelRooms[roomIndex].searchRoom.children[personIndex],
          } as FormSearchPerson;
          personTypeIndex++;
          newChildren.push(newAdult);
        }
      }
      return newChildren;
    });

    const formPersons = computed(() => {
      return [...formAdults.value, ...formChildren.value];
    });

    const bookTravel = () => {
      paymentProcessing.value = true;
      const newBookingRequest: SearchTravelBookingRequest = {
        bookingSelection: searchBookingOverview.value.bookingSelection,
        customer: null,
        href: window.location.href
      }
      TravelService.bookTravel(newBookingRequest).then((response: IResponseData<SearchTravelBookingResponse>) => {
        if (response.data.success) {
          window.location.href = response.data.result.redirectUrl;
        } else {
          alertMessage.value = response.data.exception.message;
          isShowAlert.value = true;
        }
      }).finally(() => {
        paymentProcessing.value = false;
      });
    };

    return {
      query,
      searchBookingOverview,
      addedEmails,
      selectedTransfer,
      formPersons,
      addEmail,
      formatDateTime,
      selectedPensionName,
      hasAcceptedConditions,
      bookTravel,
      hasAllValidEmails,
      updateEmail,
      deleteAddedEmail,
      isShowAlert,
      alertMessage,
      closeAlert,
      hotelLabel,
      paymentProcessing
    };
  },
});
